/**
 * Module dependencies.
 */

import { IconButton, Image, Type, icons } from 'src/components/core';
import { ReactElement } from 'react';
import { color, media, units } from '@untile/react-components';
import { prop, theme } from 'styled-tools';
import Container from 'src/components/core/layout/container';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  bgColor: string;
  className?: string;
  imageUrl: string;
  nextSectionUrl: string;
  title: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  padding-bottom: ${units(8)};
  position: relative;
  width: 100vw;

  ${media.min('md')`
    padding-bottom: ${units(18)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '.          .            image        image'
    'background background   image        image'
    'background background   .            .'
    '.          titleWrapper titleWrapper .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 21.3vw 1fr ${theme(
      'grid.gutterMobile'
    )}px;
  grid-template-rows: ${units(6.5)} repeat(3, max-content);

  ${media.min('md')`
    grid-template-areas:
      '. .          .          image      . . .            .'
      '. background background background . . .            .'
      '. background background background . . titleWrapper .'
      '. background background background . . titleWrapper .';
    grid-template-columns: 1fr 2fr 7px 3.2fr 0.8fr 7px 4fr 1fr;
    grid-template-rows: ${units(11)} repeat(3, max-content);
  `}
`;

/**
 * `Background` styled component.
 */

const Background = styled.div<{ bgColor: string }>`
  background-color: ${prop('bgColor')};
  grid-area: background;
  padding-bottom: 84.7%;
  position: relative;
  z-index: -1;

  ${media.max('md')`
    grid-column: 1 / -1;
    grid-row: 2 / 4;
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  align-self: start;
  grid-area: image;
  overflow: hidden;
  padding-bottom: 100%;
  position: relative;

  ${media.min('md')`
    grid-row: 1 / 3;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: titleWrapper;

  ${media.min('md')`
    margin-top: -${units(11)};
  `}
`;

/**
 * `Label` styled component.
 */

const Label = styled(Type.Paragraph)`
  color: ${color('grey400')};
  margin-bottom: ${units(2)};
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  margin-bottom: ${units(2)};
`;

/**
 * `StyledIconButton` styled component.
 */

const StyledIconButton = styled(IconButton)`
  display: inline-block;
  height: ${units(6)};
  padding: 0;
`;

/**
 * `NextSection` component.
 */

const NextSection = (props: Props): ReactElement => {
  const { bgColor, className, imageUrl, nextSectionUrl, title } = props;
  const { translate } = useTranslate();
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  return (
    <Wrapper className={className}>
      <StyledContainer>
        <Grid>
          <Background bgColor={bgColor} />

          <ImageWrapper
            data-scroll
            data-scroll-speed={1}
          >
            <Image
              alt={title}
              fill
              src={imageUrl}
              style={{ objectFit: 'cover' }}
            />
          </ImageWrapper>

          <TitleWrapper>
            <Label {...animProps}>{translate('common:labels.next')}</Label>

            <Title {...animProps}>{title}</Title>

            <div {...animProps}>
              <StyledIconButton
                href={nextSectionUrl}
                icon={icons.arrowRightLarge}
                iconSize={units(6)}
              />
            </div>
          </TitleWrapper>
        </Grid>
      </StyledContainer>
    </Wrapper>
  );
};

/**
 * Export `NextSection` component.
 */

export default NextSection;
